import jsPDF from 'jspdf'

const COLORS = {
  primary: [45, 80, 56],
  accent: [234, 115, 23],
  text: [55, 65, 60],
  lightText: [120, 130, 125],
  sage: [200, 215, 200],
  white: [255, 255, 255],
}

function addHeader(doc: jsPDF, text: string, y: number): number {
  doc.setFontSize(22)
  doc.setTextColor(...(COLORS.primary as [number, number, number]))
  doc.setFont('helvetica', 'bold')
  doc.text(text, 20, y)
  // Underline
  doc.setDrawColor(...(COLORS.sage as [number, number, number]))
  doc.setLineWidth(0.5)
  doc.line(20, y + 3, 190, y + 3)
  return y + 14
}

function addSubheader(doc: jsPDF, text: string, y: number): number {
  doc.setFontSize(14)
  doc.setTextColor(...(COLORS.accent as [number, number, number]))
  doc.setFont('helvetica', 'bold')
  doc.text(text, 20, y)
  return y + 8
}

function addBody(doc: jsPDF, text: string, y: number, indent = 20): number {
  doc.setFontSize(11)
  doc.setTextColor(...(COLORS.text as [number, number, number]))
  doc.setFont('helvetica', 'normal')
  const lines = doc.splitTextToSize(text, 170 - (indent - 20))
  doc.text(lines, indent, y)
  return y + lines.length * 6
}

function addBullet(doc: jsPDF, text: string, y: number, icon = '•'): number {
  doc.setFontSize(11)
  doc.setTextColor(...(COLORS.accent as [number, number, number]))
  doc.setFont('helvetica', 'bold')
  doc.text(icon, 24, y)
  doc.setTextColor(...(COLORS.text as [number, number, number]))
  doc.setFont('helvetica', 'normal')
  const lines = doc.splitTextToSize(text, 155)
  doc.text(lines, 32, y)
  return y + lines.length * 6 + 2
}

function checkPageBreak(doc: jsPDF, y: number, needed = 30): number {
  if (y > 270 - needed) {
    doc.addPage()
    return 25
  }
  return y
}

export function generatePlanPDF() {
  const doc = new jsPDF()

  // ===== COVER PAGE =====
  // Background rectangle
  doc.setFillColor(...(COLORS.primary as [number, number, number]))
  doc.rect(0, 0, 210, 297, 'F')

  // Decorative circle
  doc.setFillColor(255, 255, 255)
  doc.setGState(doc.GState({ opacity: 0.05 }))
  doc.circle(150, 50, 80, 'F')
  doc.circle(60, 250, 60, 'F')
  doc.setGState(doc.GState({ opacity: 1 }))

  // Title
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(42)
  doc.setFont('helvetica', 'bold')
  doc.text('14 Días de', 20, 90)
  doc.setFontSize(48)
  doc.text('Desinflamación', 20, 110)

  // Subtitle
  doc.setFontSize(14)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(200, 215, 200)
  const subtitle =
    'Tu guía completa para reducir la inflamación con comida real. Un plan simple, delicioso y efectivo para recuperar tu bienestar.'
  const subtitleLines = doc.splitTextToSize(subtitle, 140)
  doc.text(subtitleLines, 20, 130)

  // Badge
  doc.setFillColor(234, 115, 23)
  doc.roundedRect(20, 160, 80, 12, 6, 6, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.text('Plan Antiinflamatorio', 28, 168)

  // Footer text
  doc.setTextColor(200, 215, 200)
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.text(
    'Consulta a un profesional de la salud antes de iniciar cualquier dieta.',
    20,
    275,
  )

  // ===== PAGE 2: RULES =====
  doc.addPage()
  let y = 25

  y = addHeader(doc, 'Reglas Simples', y)
  y = addBody(
    doc,
    'Para los próximos 14 días, enfócate en nutrir tu cuerpo y darle un descanso de los alimentos inflamatorios.',
    y,
  )
  y += 4

  // Hydration box
  doc.setFillColor(230, 240, 255)
  doc.roundedRect(20, y, 170, 16, 4, 4, 'F')
  doc.setFontSize(12)
  doc.setTextColor(30, 60, 120)
  doc.setFont('helvetica', 'bold')
  doc.text('💧 Hidratación: ', 26, y + 10)
  doc.setFont('helvetica', 'normal')
  doc.text('1.5–2.5 L/día (ajusta por clima/actividad)', 72, y + 10)
  y += 24

  y = addSubheader(doc, '✅ Prioriza', y)
  const prioriza = [
    'Verduras (la mitad del plato): hojas verdes, brócoli, coliflor, pimientos',
    'Frutas: frutos rojos, manzana, cítricos',
    'Proteínas: pescado (salmón/sardina), pollo/pavo, huevos, legumbres, tofu',
    'Grasas buenas: aceite de oliva, aguacate, nueces/almendras, chía/linaza',
    'Carbohidratos integrales: avena, quinoa, arroz integral, camote',
    'Especias antiinflamatorias: cúrcuma, jengibre, canela, ajo',
  ]
  for (const item of prioriza) {
    y = checkPageBreak(doc, y)
    y = addBullet(doc, item, y, '✓')
  }
  y += 6

  y = addSubheader(doc, '❌ Reduce o Evita', y)
  const reduce = [
    'Bebidas azucaradas, dulces, pan/pasta blancos',
    'Frituras, embutidos, comida rápida, snacks ultraprocesados',
    'Alcohol (ideal: 0 por 14 días)',
    'Exceso de harinas/refinados',
  ]
  for (const item of reduce) {
    y = checkPageBreak(doc, y)
    y = addBullet(doc, item, y, '✗')
  }

  // ===== MEAL PLAN PAGES =====
  const mealPlan: Record<number, { type: string; desc: string }[]> = {
    1: [
      { type: 'Desayuno', desc: 'Avena con chía + frutos rojos + nueces' },
      { type: 'Comida', desc: 'Salmón + ensalada grande + quinoa' },
      { type: 'Cena', desc: 'Sopa de lentejas con verduras' },
      { type: 'Snack', desc: 'Yogur natural o kéfir (si lo toleras) + canela' },
    ],
    2: [
      { type: 'Desayuno', desc: 'Huevos con espinaca + aguacate' },
      {
        type: 'Comida',
        desc: 'Pollo a la plancha + verduras asadas + arroz integral',
      },
      {
        type: 'Cena',
        desc: 'Ensalada de atún/sardinas + garbanzos + aceite de oliva',
      },
      { type: 'Snack', desc: 'Manzana + almendras' },
    ],
    3: [
      {
        type: 'Desayuno',
        desc: 'Smoothie verde (espinaca + limón + pepino) + proteína',
      },
      {
        type: 'Comida',
        desc: 'Tacos en hoja de lechuga con pavo + pico de gallo',
      },
      { type: 'Cena', desc: 'Tofu o pollo salteado con brócoli y jengibre' },
      { type: 'Snack', desc: 'Zanahoria + hummus' },
    ],
    4: [
      { type: 'Desayuno', desc: 'Yogur natural/kéfir + frutos rojos + linaza' },
      {
        type: 'Comida',
        desc: 'Ensalada "bowl" (quinoa + frijoles + verduras + aguacate)',
      },
      { type: 'Cena', desc: 'Crema de calabaza (sin crema) + ensalada' },
      { type: 'Snack', desc: 'Nueces' },
    ],
    5: [
      { type: 'Desayuno', desc: 'Omelette con champiñones + tomate' },
      { type: 'Comida', desc: 'Pescado blanco + camote + ensalada' },
      { type: 'Cena', desc: 'Garbanzos guisados con verduras y cúrcuma' },
      { type: 'Snack', desc: 'Cítrico (naranja/mandarina)' },
    ],
    6: [
      { type: 'Desayuno', desc: 'Avena nocturna con canela + chía' },
      {
        type: 'Comida',
        desc: 'Pavo/pollo + ensalada griega (aceitunas, pepino) + aceite de oliva',
      },
      { type: 'Cena', desc: 'Salteado de verduras + huevo o tofu' },
      { type: 'Snack', desc: 'Pepino con limón y sal' },
    ],
    7: [
      { type: 'Desayuno', desc: 'Pan integral 100% + aguacate + huevo' },
      { type: 'Comida', desc: 'Sopa de verduras + ensalada + proteína' },
      { type: 'Cena', desc: 'Salmón/sardinas + espárragos + quinoa' },
      { type: 'Snack', desc: 'Chocolate amargo (85%+) 1–2 cuadritos' },
    ],
  }

  doc.addPage()
  y = 25
  y = addHeader(doc, 'Tu Menú de 7 Días', y)
  y = addBody(
    doc,
    'Repite este menú en la semana 2 o intercambia comidas según tus preferencias.',
    y,
  )
  y += 4

  for (let day = 1; day <= 7; day++) {
    y = checkPageBreak(doc, y, 50)

    // Day header with background
    doc.setFillColor(...(COLORS.sage as [number, number, number]))
    doc.roundedRect(20, y - 5, 170, 10, 3, 3, 'F')
    doc.setFontSize(13)
    doc.setTextColor(...(COLORS.primary as [number, number, number]))
    doc.setFont('helvetica', 'bold')
    doc.text(`Día ${day}`, 26, y + 2)
    y += 12

    for (const meal of mealPlan[day]) {
      y = checkPageBreak(doc, y)
      doc.setFontSize(11)
      doc.setTextColor(...(COLORS.accent as [number, number, number]))
      doc.setFont('helvetica', 'bold')
      doc.text(`${meal.type}:`, 28, y)
      doc.setTextColor(...(COLORS.text as [number, number, number]))
      doc.setFont('helvetica', 'normal')
      const mealLines = doc.splitTextToSize(meal.desc, 120)
      doc.text(mealLines, 60, y)
      y += mealLines.length * 6 + 2
    }
    y += 4
  }

  // ===== SHOPPING LIST =====
  doc.addPage()
  y = 25
  y = addHeader(doc, 'Lista de Compras Económica', y)
  y = addBody(
    doc,
    'Ingredientes accesibles para tus 14 días de desinflamación.',
    y,
  )
  y += 4

  const shoppingCategories = [
    {
      title: 'Proteínas Económicas',
      items: [
        'Huevos (cartón grande)',
        'Atún o sardinas en lata',
        'Pechuga o muslos de pollo',
        'Lentejas y garbanzos (secos)',
        'Tofu (opcional)',
      ],
    },
    {
      title: 'Verduras',
      items: [
        'Espinaca o acelga',
        'Brócoli',
        'Calabacín / Calabaza',
        'Zanahorias',
        'Tomate, cebolla y ajo',
        'Pepino',
      ],
    },
    {
      title: 'Frutas',
      items: [
        'Limones',
        'Manzanas',
        'Naranjas o mandarinas',
        'Frutos rojos (congelados)',
        'Aguacate',
      ],
    },
    {
      title: 'Granos, Semillas y Grasas',
      items: [
        'Avena natural',
        'Arroz integral',
        'Semillas de chía y linaza',
        'Nueces o almendras',
        'Aceite de oliva extra virgen',
      ],
    },
    {
      title: 'Despensa y Extras',
      items: [
        'Canela en polvo',
        'Cúrcuma en polvo',
        'Jengibre',
        'Yogur natural sin azúcar o Kéfir',
        'Chocolate amargo (85%+)',
      ],
    },
  ]

  for (const cat of shoppingCategories) {
    y = checkPageBreak(doc, y, 40)
    y = addSubheader(doc, cat.title, y)
    for (const item of cat.items) {
      y = checkPageBreak(doc, y)
      doc.setFontSize(11)
      doc.setTextColor(...(COLORS.text as [number, number, number]))
      doc.setFont('helvetica', 'normal')
      doc.text(`☐  ${item}`, 28, y)
      y += 7
    }
    y += 4
  }

  // ===== EXTRAS =====
  y = checkPageBreak(doc, y, 60)
  y += 4
  y = addHeader(doc, 'Extras que Ayudan Mucho', y)

  const extras = [
    {
      title: 'Movimiento Diario',
      desc: 'Caminar 20–30 min diarios. Ayuda a la digestión y reduce el estrés.',
    },
    {
      title: 'Descanso Profundo',
      desc: 'Dormir 7–9 horas. El cuerpo se repara y desinflama mientras duermes.',
    },
    {
      title: 'Fuerza Ligera',
      desc: '2–3 sesiones/semana (sentadillas, ligas, pesas). Mejora la sensibilidad a la insulina.',
    },
  ]

  for (const extra of extras) {
    y = checkPageBreak(doc, y)
    y = addSubheader(doc, extra.title, y)
    y = addBody(doc, extra.desc, y, 28)
    y += 4
  }

  // ===== SAVE =====
  doc.save('Plan-Antiinflamatorio-14-Dias.pdf')
}
